import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { ArrowForwardIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { EDITORIAL_HUB_FAMILY_PRESENTATION } from "@/features/editorial-hub/editorial-hub-families";
import { getFeaturePageLandingQueryOptions } from "@/features/feature-page-landing/feature-page-landing-query";
import { FeaturePageVisual } from "@/features/feature-page/feature-page-visual";
import { cn } from "@/lib/utils";
import { buildFeaturePageLandingPath } from "@garimpa/lib/buildFeaturePageLandingPath";
import { type FeaturePageFamily } from "@garimpa/lib/featurePage";
import { type ViewerLocale } from "@garimpa/lib/viewerPreferences";

export function EditorialHubFamilyCard({
  family,
  locale,
}: {
  family: FeaturePageFamily;
  locale: ViewerLocale;
}) {
  const t = useTranslations("editorialHub");
  const landingT = useTranslations("featurePageLanding");
  const landingQuery = useQuery(getFeaturePageLandingQueryOptions({ family, locale }));
  const previewImage = landingQuery.data?.items.find((item) => item.image)?.image;
  const presentation = EDITORIAL_HUB_FAMILY_PRESENTATION[family];
  const FamilyIcon = presentation.Icon;

  return (
    <Link
      to={buildFeaturePageLandingPath(locale, family)}
      className={cn(
        "group relative isolate flex h-full min-h-80 overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br p-7 text-white shadow-xl transition duration-500 hover:-translate-y-1 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-96 sm:p-9",
        presentation.className,
      )}
    >
      {previewImage ? (
        <FeaturePageVisual
          visual={{
            ...previewImage,
            sizes: "(min-width: 768px) 50vw, 100vw",
          }}
          decorative
          className="absolute inset-0 -z-20 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
        />
      ) : null}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black via-black/55 to-black/10" />
      <div className="absolute -right-16 -top-16 -z-10 size-56 rounded-full border border-white/10 bg-white/5 transition duration-700 group-hover:scale-125" />

      <div className="flex w-full flex-col justify-between gap-12">
        <span
          className={cn(
            "grid size-14 place-items-center rounded-2xl shadow-lg transition duration-500 group-hover:-rotate-3 group-hover:scale-105",
            presentation.iconClassName,
          )}
        >
          <FamilyIcon className="text-3xl" aria-hidden="true" />
        </span>

        <div>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl">
            {landingT(`${family}.title`)}
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            {landingT(`${family}.description`)}
          </p>
          <span className="mt-7 inline-flex items-center gap-2 font-bold">
            {t("browseFamily", { family })}
            <ArrowForwardIcon
              className="text-xl transition-transform group-hover:translate-x-1"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}
